import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { DropDownComponent } from "../../dropdown/DropDownComponent";

export const DEFAULT_SELECT_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    border: "1px solid #c9d8e6",
    borderRadius: 7,
    backgroundColor: "#ffffff",
    boxShadow: "none",
    fontSize: 13,
  }),
  valueContainer: (base) => ({
    ...base,
    height: 30,
    padding: "0 10px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    opacity: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 30,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "5px 8px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#0054b9",
    fontSize: 13,
    fontWeight: 500,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 100000,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 100000,
    border: "1px solid #c9d8e6",
    boxShadow: "0 12px 26px rgba(36, 73, 104, 0.18)",
  }),
};

const hasValue = (field) => {
  if (field.type === "dateRange") {
    return !!(field.valueObject?.[0] || field.valueObject?.[1]);
  }
  if (Array.isArray(field.valueObject)) {
    return field.valueObject.length > 0;
  }

  if (field.valueObject && typeof field.valueObject === "object") {
    return field.valueObject.value != null && field.valueObject.value !== "";
  }
  return field.valueObject != null && field.valueObject !== "";
};

const getInitialRows = (fields, startWithFirstOnly = false) => {
  const activeFields = fields.filter(hasValue);

  if (activeFields.length > 0) {
    return activeFields.map((field, index) => ({
      id: field.value,
      field: field.value,
    }));
  }

  return [
    {
      id: fields[0].value,
      field: fields[0].value,
    },
  ];
};

const formatDateValue = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);

  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const parseLocalDate = (dateValue) => {
  if (!dateValue) return null;

  const date = new Date(dateValue + "T00:00:00");

  return isNaN(date.getTime()) ? null : date;
};
const AdvancedFilterDropdown = ({
  ApplyFilter,
  ClearFilter,
  fields,
  isApplyDisabled,
  startWithFirstOnly,
  onCloseFilter,
}) => {
  const availableFields = useMemo(() => fields.filter((field) => !field.hidden), [fields]);
  const [filterRows, setFilterRows] = useState(() =>
    getInitialRows(availableFields, startWithFirstOnly)
  );
  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

  const closeFilterDropdown = (event) => {
    event?.stopPropagation();
    onCloseFilter?.();
  };

  const getField = (fieldValue) =>
    availableFields.find((field) => field.value === fieldValue)

  const addFilterRow = () => {
    const usedFields = filterRows.map((row) => row.field);
    const nextField = availableFields.find(
      (field) => !usedFields.includes(field.value)
    );

    if (nextField) {
      setFilterRows((rows) => [
        ...rows,
        {
          id: Date.now(),
          field: nextField.value,
        },
      ]);
    }
  };

  const handleFilterFieldChange = (rowId, nextField) => {
    setFilterRows((rows) =>
      rows.map((row) =>
        row.id === rowId
          ? { ...row, field: nextField }
          : row
      )
    );
  };

  const removeFilterRow = (rowId) => {
    const rowToRemove = filterRows.find((row) => row.id === rowId);
    const fieldConfig = rowToRemove ? getField(rowToRemove.field) : null;

    if (fieldConfig) {
      fieldConfig.type === "dateRange"
        ? fieldConfig.onChange?.([null, null])
        : fieldConfig.onChange?.(null);
    }

    setFilterRows((rows) => rows.filter((row) => row.id !== rowId));
  };

  const [dateRangeError, setDateRangeError] = useState("");

  return (
    <div
      className="dropdown-menu filter-dropdown quote-filter-menu p-0 show"
      aria-labelledby="filterDropdown"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="quote-filter-panel enqiry-dropdown">
        <div className="quote-filter-header">
          <div>
            <h6>
              <i className="fas fa-filter mr-2"></i>
              Filters
            </h6>
            <p>Build focused list conditions</p>
          </div>
          <button
            className="btn filter-close-btn"
            type="button"
            onClick={closeFilterDropdown}
            title="Close filters"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="quote-filter-builder">
          {filterRows.map((row, index) => {
            const fieldConfig = getField(row.field);
            const inputId = `advanced-filter-${row.id}`;
            const usedFieldsInOtherRows = filterRows
              .filter((otherRow) => otherRow.id !== row.id)
              .map((otherRow) => otherRow.field);

            return (
              <div
                className={`quote-filter-row ${fieldConfig.type === "dateRange" ? "quote-filter-row-date align-items-start" : ""
                  }`}
                key={row.id}
              >
                <div className="filter-field-cell">
                  <label htmlFor={`${inputId}-field`}>Filter by</label>
                  <select
                    id={`${inputId}-field`}
                    name={`${inputId}-field`}
                    className="form-control"
                    value={row.field}
                    onChange={(event) => handleFilterFieldChange(row.id, event.target.value)}
                  >
                    {availableFields.map((field) => (
                      <option
                        key={field.value}
                        value={field.value}
                        disabled={usedFieldsInOtherRows.includes(field.value)}
                      >
                        {field.label}
                      </option>
                    ))}
                  </select>
                </div>

                {fieldConfig.type === "dateRange" ? (
                  <>
                    <div className="filter-date-cell">
                      <label htmlFor={`${inputId}-from`}>From</label>
                      <input
                        id={`${inputId}-from`}
                        name={`${inputId}-from`}
                        type="date"
                        className="form-control"
                        value={formatDateValue(fieldConfig.valueObject?.[0])}
                        onChange={(event) => {
                          const newDate = parseLocalDate(event.target.value);

                          fieldConfig.onChange?.([
                            newDate,
                            fieldConfig.valueObject?.[1] || null,
                          ]);
                        }}
                      />
                    </div>
                    <div className="filter-date-cell">
                      <label htmlFor={`${inputId}-to`}>To</label>
                      <input
                        id={`${inputId}-to`}
                        name={`${inputId}-to`}
                        type="date"
                        className="form-control"
                        value={formatDateValue(fieldConfig.valueObject?.[1])}
                        onChange={(event) => {
                          const fromDate = fieldConfig.valueObject?.[0];
                          const toDate = parseLocalDate(event.target.value);

                          if (fromDate && toDate && toDate < fromDate) {
                            setDateRangeError(
                              "To date should be greater than or equal to From date."
                            );

                            fieldConfig.onChange?.([
                              fromDate || null,
                              null,
                            ]);
                          } else {
                            setDateRangeError("");

                            fieldConfig.onChange?.([
                              fromDate || null,
                              toDate,
                            ]);
                          }
                        }}
                      />
                      {dateRangeError && (
                        <div className="text-danger mt-1">
                          {dateRangeError}
                        </div>
                      )}
                    </div>
                  </>
                ) : fieldConfig.type === "number" ? (
                  <div className="filter-value-cell">
                    <label htmlFor={`${inputId}-value`}>{fieldConfig.label}</label>
                    <input
                      id={`${inputId}-value`}
                      name={`${fieldConfig.value}-${index}`}
                      type="number"
                      className="form-control"
                      value={fieldConfig.valueObject ?? ""}
                      onChange={(event) => fieldConfig.onChange?.(event.target.value)}
                      placeholder={fieldConfig.placeholder || "Enter value"}
                    />
                  </div>
                ) : fieldConfig.type === "select" ? (
                  <div className="filter-value-cell">
                    <label htmlFor={`${inputId}-value`}>{fieldConfig.label}</label>
                    <Select
                      key={`${row.id}-${row.field}`}
                      inputId={`${inputId}-value`}
                      name={`${fieldConfig.value}-${index}`}
                      options={fieldConfig.options || []}
                      value={fieldConfig.valueObject}
                      onChange={fieldConfig.onChange}
                      placeholder={fieldConfig.placeholder || "All"}
                      isClearable
                      styles={DEFAULT_SELECT_STYLES}
                      menuPortalTarget={menuPortalTarget}
                      menuPosition="fixed"
                    />
                  </div>
                ) : (
                  <div className="filter-value-cell">
                    <label htmlFor={`${inputId}-value`}>{fieldConfig.label}</label>
                    <DropDownComponent
                      key={`${row.id}-${row.field}`}
                      inputId={`${inputId}-value`}
                      placeHolder={fieldConfig.placeholder || "All"}
                      pageName={fieldConfig.pageName}
                      dpDownName={fieldConfig.dpDownName}
                      stateData={fieldConfig.data}
                      controlName={`${fieldConfig.value}-${index}`}
                      handleChange={fieldConfig.onChange}
                      value={fieldConfig.valueObject}
                      valueId={fieldConfig.valueId}
                      setState={fieldConfig.setState || (() => { })}
                      multiSelect={fieldConfig.multiSelect === true}
                      checkLabel={fieldConfig.checkLabel}
                      menuPortalTarget={menuPortalTarget}
                      customStyles={DEFAULT_SELECT_STYLES}
                    />
                  </div>
                )}

                {filterRows.length > 1 && (
                  <button
                    className="btn filter-row-delete mt-4"
                    type="button"
                    onClick={() => removeFilterRow(row.id)}
                    title="Remove filter"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="quote-filter-footer">
          <button
            className="btn add-filter-row-btn"
            type="button"
            onClick={addFilterRow}
            disabled={filterRows.length >= availableFields.length}
          >
            <i className="fas fa-plus mr-2"></i>
            Add Filter
          </button>
          <div className="filter-action-group">
            <button
              className="btn reset-btn"
              type="button"
              onClick={(event) => {
                setFilterRows([
                  { id: availableFields[0].value, field: availableFields[0].value },
                ]);
                setDateRangeError("");
                ClearFilter();
                closeFilterDropdown(event);
              }}
            >
              Reset
            </button>
            <button
              className="btn apply-btn"
              type="button"
              disabled={isApplyDisabled}
              onClick={() => {
                const values = {};
                filterRows.forEach((row) => {
                  const fc = getField(row.field);
                  if (fc) values[fc.value] = fc.valueObject;
                });

                ApplyFilter(values);
                onCloseFilter?.();
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AdvancedFilterDropdown);
